import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  SvgIcon,
  Typography,
} from "@mui/material";
import Layout from "components/layout/layout";
import { CustomBreadcrumb } from "components/shared/custom-breadcrumb";
import { CustomStepper } from "components/shared/custom-stepper";
import { FundDashboardPage } from "constants/pages";
import { ReactComponent as CashIcon } from "./../../../assets/Icons/Cash.svg";
import { ReactComponent as EditIcon } from "./../../../assets/Icons/edit.svg";
import { ReactComponent as EyeOpen } from "./../../../assets/Icons/eye-open.svg";

import { ReactComponent as Localization } from "./../../../assets/Icons/localization.svg";
import { CustomAccordion } from "components/shared/custom-accordion";
import { GrantDetails } from "./grant-request-details";
import { GrantAcknowledgementDetails } from "./grant-request-acknowledgement";
import { GrantSummary } from "./grant-request-summary";
import { GrantSuccess } from "./grant-request-success";
import { useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { useGetCharityQuery } from "hooks/use-services/charity.service";
import { neutral, primary, warm } from "style/color";
import {
  CashInfo,
  GRANT_PURPOSE_ENUM,
  GRANT_REQUEST_TRANSACTION_TYPE_ENUM,
  GRANT_REQUEST_TYPE_ENUM,
  GrantRequestInput,
  InvestmentInfo,
} from "types/grant-request";
import { AuthContext } from "providers/userProvider";
import { AuthData } from "types/auth";
import { moneyFormat } from "utils/money-format";
import { useMakeIGrantRequestMutation } from "hooks/use-services/grant-request.service";
import { GrantRequestCharityList } from "./grant-request-charities-list";
import { einFormat } from "types/charity";
import { INVESTMENT_REQUEST_INTEREST_ENUM } from "types/investment-request";
import AdminUserSelection from "components/shared/fund-users-drop-down";
import awsExports from "../../../aws-exports";
import { User } from "types/user";
import { compileHtmlToPDF } from "utils/create-pdf";
import { previewGrantLetterTemplateAPI } from "api/grant-request.api";

const GrantRequestHomePage = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const { selectedAccount, userData } = useContext<AuthData>(AuthContext);
  const [ein, setEin] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [exchangeValue, setExchangeValue] = useState<boolean>(false);
  const [obligationPayment, setObligationPayment] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [letterPreviewTemplate, setLetterPreviewTemplate] = useState<any[]>([]);
  const [letterPreviewError, setLetterPreviewError] = useState(false);
  const [grantRequest, setGrantRequest] = useState<GrantRequestInput>({
    sub: userData?.sub!,
    fundId: selectedAccount?.fundAccountId!,
    type: GRANT_REQUEST_TYPE_ENUM.ONETIME,
    transactionType: GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH,
    toInform: "",
    information: {
      requestedAmount: "" as any,
    },
    scheduleInformation: {
      now: true,
      executionDate: new Date(),
    },
    acknowledgement: {
      anonymous: false,
      showFundName: true,
      showDonorAddress: false,
      showDonorAdress: false,
      showNameInRecommendation: false,
      nameInRecommendation: "",
      showNameInRecognition: false,
      nameInRecognition: "",
      donorAddress: "",
      inHonorOf: "",
      inMemoryOf: "",
      thirdPartiesEmails: [""],
      visibility: true,
      donorSelections: [],
      showFundNameInRecommendation: true,
      showFundNameInRecognition: false,
    },
    grantPurpose: {
      type: GRANT_PURPOSE_ENUM.UNRESTRICTED,
      note: "",
    },
    emailToNotify: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const initialized = useRef(false);
  const { data: charity, isSuccess } = useGetCharityQuery(ein);
  const { mutate, isLoading } = useMakeIGrantRequestMutation(ein);

  useEffect(() => {
    if (userData?.sub !== awsExports.neta_super_admin_user_sub) {
      if (userData?.user) {
        setSelectedUser(userData.user);
      } else {
        setSelectedUser(undefined);
      }
    }
  }, [userData?.sub, userData?.user]);

  const handleTransactionTypeChange = (
    transactionType: GRANT_REQUEST_TRANSACTION_TYPE_ENUM,
    information: any
  ) => {
    setGrantRequest({
      ...grantRequest,
      transactionType: transactionType,
      information: information,
      type: GRANT_REQUEST_TYPE_ENUM.ONETIME,
    });
  };
  const handleTypeChange = (
    type: GRANT_REQUEST_TYPE_ENUM,
    scheduleInformation: any
  ) => {
    setGrantRequest({
      ...grantRequest,
      type: type,
      scheduleInformation: scheduleInformation,
    });
  };
  const handleGrantRequestChange = (key: string, value: any) => {
    setGrantRequest({
      ...grantRequest,
      [key]: value,
    });
  };
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      if (searchParams && searchParams.get("ein")) {
        setEin(searchParams.get("ein") || "");
        setActiveStep(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const getAmount = () => {
    if (
      grantRequest.transactionType === GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH
    ) {
      return `$${moneyFormat(
        +(grantRequest.information as CashInfo).requestedAmount
      )}`;
    }
    if (
      grantRequest.transactionType ===
      GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT
    ) {
      const interestedIn = (grantRequest.information as InvestmentInfo)
        .interestedIn;
      if (interestedIn === INVESTMENT_REQUEST_INTEREST_ENUM.AMOUNT) {
        return `$${moneyFormat(
          +(grantRequest.information as InvestmentInfo).requestedAmount
        )}`;
      } else {
        return `${moneyFormat(
          +(grantRequest.information as InvestmentInfo).units,
          4,
          2
        )} unit(s) of ${(grantRequest.information as InvestmentInfo).ticker} `;
      }
    }
  };

  useEffect(() => {
    if (
      !isSuccess ||
      !charity ||
      !selectedAccount ||
      !userData?.user ||
      !ein ||
      !userData?.sub
    ) {
      setLetterPreviewTemplate([]);
      setLetterPreviewError(false);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setLetterPreviewError(false);
      try {
        const fundId = grantRequest.fundId ?? selectedAccount.fundAccountId;
        if (!fundId) {
          return;
        }
        const payload: GrantRequestInput = {
          ...grantRequest,
          sub: grantRequest.sub || userData.sub!,
          fundId,
        };
        const template = await previewGrantLetterTemplateAPI(ein, payload);
        if (!cancelled) {
          setLetterPreviewTemplate(Array.isArray(template) ? template : []);
        }
      } catch {
        if (!cancelled) {
          setLetterPreviewError(true);
          setLetterPreviewTemplate((prev) => (prev.length > 0 ? prev : []));
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    grantRequest,
    charity,
    ein,
    isSuccess,
    selectedAccount,
    userData?.user,
    userData?.sub,
  ]);

  const openLetterPreviewPDF = async () => {
    if (!selectedAccount || !userData?.user || !charity || !ein || !userData.sub) {
      return;
    }
    try {
      const fundId = grantRequest.fundId ?? selectedAccount.fundAccountId;
      if (!fundId) {
        return;
      }
      const payload: GrantRequestInput = {
        ...grantRequest,
        sub: grantRequest.sub || userData.sub!,
        fundId,
      };
      const template = await previewGrantLetterTemplateAPI(ein, payload);
      compileHtmlToPDF(template);
    } catch {
      // Preview PDF is best-effort; inline preview shows error state
    }
  };

  const submit = () => {
    window.scrollTo(0, 0);
    let newGrantRequest = grantRequest;
    if (
      grantRequest.transactionType === GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH
    ) {
      (newGrantRequest.information as CashInfo).requestedAmount = +(
        grantRequest.information as CashInfo
      ).requestedAmount;
    }
    if (
      grantRequest.transactionType ===
      GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT
    ) {
      (newGrantRequest.information as InvestmentInfo).requestedAmount = +(
        grantRequest.information as InvestmentInfo
      ).requestedAmount;
      (newGrantRequest.information as InvestmentInfo).units = +(
        grantRequest.information as InvestmentInfo
      ).units;
    }
    if (
      grantRequest.acknowledgement.thirdPartiesEmails.length === 1 &&
      grantRequest.acknowledgement.thirdPartiesEmails[0] === ""
    ) {
      newGrantRequest.acknowledgement.thirdPartiesEmails = [];
    }
    newGrantRequest.fundId = selectedAccount?.fundAccountId!;
    newGrantRequest.sub = userData?.sub!;
    newGrantRequest.emailToNotify = selectedUser?.email || "";
    mutate(newGrantRequest, {
      onSuccess: () => setActiveStep(3),
    });
  };
  const selectCharity = (ein: string) => {
    setEin(ein);
    setSearchParams({ ein: ein });
    setActiveStep(1);
  };
  return (
    <Layout>
      {(handleOpen: (value: boolean) => void) => (
        <Box sx={{ m: 5 }}>
          <CustomBreadcrumb
            title={"Back to fund dashboard "}
            navigateTo={FundDashboardPage}
          />
          <CustomStepper
            stepsTitles={[
              "Select charity",
              "Enter amount and details",
              "Confirm",
              "Request review",
            ]}
            activeStep={activeStep}
          />
          {activeStep === 0 && (
            <GrantRequestCharityList
              selectCharity={selectCharity}
              handleOpen={handleOpen}
            />
          )}
          {activeStep === 1 && isSuccess && (
            <Box
              component="form"
              autoComplete="off"
              onSubmit={(event) => {
                window.scrollTo(0, 0);
                setActiveStep(2);
              }}
            >
              <Typography variant="subtitle3" color={neutral[900]} gutterBottom>
                Charity information
              </Typography>
              <Typography variant="h3" color={neutral[900]}>
                {charity.name}
              </Typography>
              <Typography variant="bodyCopyRegular" color={neutral[600]}>
                {charity.country === "USA"
                  ? einFormat(charity.ein)
                  : charity.ein}
              </Typography>
              <Grid container py={1} spacing={1}>
                <Grid item>
                  <Localization />
                </Grid>
                <Grid item>
                  <Typography variant="bodyCopyRegular" color={neutral[600]}>
                    {charity.street} {charity.street2},<br></br>
                    {charity.city}, {charity.state} {charity.zip},{" "}
                    {charity.country}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={8}>
                  <CustomAccordion
                    title={"Enter grant details"}
                    defaultExpanded={true}
                  >
                    <GrantDetails
                      grantRequest={grantRequest}
                      setError={setError}
                      handleGrantRequestChange={handleGrantRequestChange}
                      handleTransactionTypeChange={handleTransactionTypeChange}
                      handleTypeChange={handleTypeChange}
                    />
                  </CustomAccordion>
                  <CustomAccordion
                    title={"Let’s create your grant letter"}
                    defaultExpanded={true}
                    compactContent={true}
                    headerAction={
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EyeOpen />}
                        onClick={openLetterPreviewPDF}
                      >
                        Preview letter
                      </Button>
                    }
                  >
                    <GrantAcknowledgementDetails
                      charity={charity}
                      grantRequest={grantRequest}
                      handleGrantRequestChange={handleGrantRequestChange}
                      onPreviewGrantLetterPdf={openLetterPreviewPDF}
                    />
                  </CustomAccordion>
                  <Box sx={{ display: "flex", flexDirection: "row", pt: 5 }}>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      sx={{ paddingY: 2, paddingX: 3 }}
                      disabled={error}
                    >
                      Next
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  {((grantRequest.transactionType ===
                    GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH &&
                    (grantRequest.information as CashInfo).requestedAmount !==
                      0) ||
                    (grantRequest.transactionType ===
                      GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT &&
                      (grantRequest.information as InvestmentInfo)
                        .requestedAmount !== 0)) && (
                      <Card sx={{ boxShadow: "none", mt: 2 }}>
                        <CardContent sx={{ p: 5, "&:last-child": { pb: 5 } }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ color: `neutral.main` }}
                            gutterBottom
                          >
                            Summary
                          </Typography>
                          <Typography
                            className="title"
                            variant="smallCopy"
                            sx={{ color: `neutral.main` }}
                            paragraph
                          >
                            To
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            sx={{ color: `neutral.main` }}
                            gutterBottom
                          >
                            {charity.name}
                          </Typography>

                          <Divider
                            sx={{
                              backgroundColor: warm[600],
                              borderWidth: "1px",
                              my: 2,
                            }}
                          />
                          <Typography
                            className="title"
                            variant="smallCopy"
                            sx={{ color: `neutral.main` }}
                            paragraph
                          >
                            via
                          </Typography>
                          <Typography
                            variant="bodyCopyBold"
                            sx={{
                              color: `neutral.main`,
                              display: "flex",
                              alignItems: "center",
                            }}
                            paragraph
                          >
                            {grantRequest.transactionType ===
                              GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH && (
                                <>
                                  <SvgIcon
                                    sx={{
                                      mr: 2,
                                      "&  svg  path": {
                                        fill: neutral[900],
                                      },
                                      "&  svg  circle": {
                                        stroke: neutral[900],
                                      },
                                    }}
                                  >
                                    {" "}
                                    <CashIcon />
                                  </SvgIcon>{" "}
                                  Cash{" "}
                                </>
                              )}
                            {grantRequest.transactionType ===
                              GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT && (
                                <>
                                  <SvgIcon
                                    sx={{
                                      mr: 2,
                                      "&  svg  path": {
                                        fill: neutral[900],
                                      },
                                      "&  svg  circle": {
                                        stroke: neutral[900],
                                      },
                                    }}
                                  >
                                    {" "}
                                    <CashIcon />
                                  </SvgIcon>{" "}
                                  Investments{" "}
                                </>
                              )}
                          </Typography>
                          {grantRequest.transactionType ===
                            GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT && (
                              <>
                                <Divider
                                  sx={{
                                    backgroundColor: warm[600],
                                    borderWidth: "1px",
                                    my: 2,
                                  }}
                                />
                                <Typography
                                  className="title"
                                  variant="smallCopyBold"
                                  sx={{ color: neutral[600] }}
                                  paragraph
                                >
                                  {
                                    (grantRequest.information as InvestmentInfo)
                                      .ticker
                                  }
                                </Typography>
                                <Typography
                                  variant="bodyCopyRegular"
                                  sx={{ color: `neutral.main` }}
                                  paragraph
                                >
                                  $
                                  {moneyFormat(
                                    +(grantRequest.information as InvestmentInfo)
                                      .requestedAmount
                                  )}
                                </Typography>
                              </>
                            )}
                          <Divider
                            sx={{
                              backgroundColor: warm[600],
                              borderWidth: "1px",
                              my: 2,
                            }}
                          />
                          <Typography
                            className="title"
                            variant="smallCopy"
                            sx={{ color: `neutral.main` }}
                            paragraph
                          >
                            Total amount
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            sx={{ color: primary[500] }}
                            gutterBottom
                          >
                            {grantRequest.transactionType ===
                              GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH && (
                                <>
                                  $
                                  {moneyFormat(
                                    +(grantRequest.information as CashInfo)
                                      .requestedAmount
                                  )}
                                </>
                              )}
                            {grantRequest.transactionType ===
                              GRANT_REQUEST_TRANSACTION_TYPE_ENUM.INVESTMENT && (
                                <>
                                  -$
                                  {moneyFormat(
                                    +(grantRequest.information as InvestmentInfo)
                                      .requestedAmount
                                  )}
                                </>
                              )}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  <Card
                    sx={{
                      boxShadow: "none",
                      mt: 2,
                      border: "1px solid",
                      borderColor: neutral[200],
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Typography
                        variant="subtitle3"
                        sx={{ color: neutral[900] }}
                        gutterBottom
                      >
                        Letter Preview
                      </Typography>
                      <Typography
                        variant="bodyCopyRegular"
                        color={neutral[600]}
                        sx={{ mb: 2 }}
                        paragraph
                      >
                        This preview reflects the PDF generated from your current
                        inputs.
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: neutral[200],
                          borderRadius: 1,
                          backgroundColor: "#fff",
                          overflow: "hidden",
                          height: { xs: 420, sm: 520 },
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            p: 3,
                          }}
                        >
                          {letterPreviewError && letterPreviewTemplate.length === 0 && (
                            <Typography color="error" variant="bodyCopyRegular">
                              Unable to load letter preview. Please try again.
                            </Typography>
                          )}
                          {letterPreviewTemplate.map((item, index) => {
                            if (!item || typeof item !== "object") {
                              return null;
                            }
                            if ("image" in item && typeof item.image === "string") {
                              return (
                                <Box
                                  key={`preview-image-${index}`}
                                  component="img"
                                  src={item.image}
                                  alt="Neta logo"
                                  sx={{ width: 140, mb: 2, display: "block" }}
                                />
                              );
                            }
                            if ("text" in item && typeof item.text === "string") {
                              return (
                                <Typography
                                  key={`preview-text-${index}`}
                                  sx={{
                                    color: neutral[900],
                                    fontSize: 12,
                                    lineHeight: 1.55,
                                    mb: 1.5,
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {item.text}
                                </Typography>
                              );
                            }
                            return null;
                          })}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 2 && (
            <>
              <Grid>
                <Card sx={{ boxShadow: "none", mt: 2 }}>
                  <CardContent sx={{ p: 5, "&:last-child": { pb: 5 } }}>
                    {charity && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{ color: `neutral.main` }}
                            variant="subtitle1"
                          >
                            Summary
                          </Typography>
                          <IconButton
                            className="black"
                            edge="end"
                            onClick={() => setActiveStep(1)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Box>
                        <GrantSummary
                          grantRequest={grantRequest}
                          charity={charity}
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
                <Grid item xs={12}>
                  {userData?.sub === awsExports.neta_super_admin_user_sub && (
                    <AdminUserSelection
                      fundAccountId={selectedAccount?.fundAccountId as number}
                      setSelectedUser={setSelectedUser}
                      selectedAccount={selectedUser}
                    />
                  )}
                </Grid>
                <Grid
                  container
                  display={"flex"}
                  sx={{
                    paddingTop: 2,
                  }}
                >
                  <Grid item xs={12}>
                    <Typography variant="subtitle3">
                      Please confirm the following
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      value={exchangeValue}
                      name="exchangeValue"
                      onChange={(event: any) =>
                        setExchangeValue(event.target.checked)
                      }
                      control={<Checkbox checked={exchangeValue} />}
                      label={
                        <Typography
                          variant="bodyCopyRegular"
                          sx={{ paddingTop: 1 }}
                          color={neutral[900]}
                        >
                          You do not expect you, or any other person related to
                          you or to this Fund, to receive anything of value in
                          exchange for this grant. Things of value include (but
                          are not limited to) cash or goods and services,
                          including meals, items, purchased at auctions,
                          tickets, voucher or admission to events.
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      value={obligationPayment}
                      name="obligationPayment"
                      onChange={(event: any) =>
                        setObligationPayment(event.target.checked)
                      }
                      control={<Checkbox checked={obligationPayment} />}
                      label={
                        <Typography
                          variant="bodyCopyRegular"
                          color={neutral[900]}
                        >
                          This grant from Neta Foundation will not be used to
                          pay any personal legal obligation you may have,
                          including any legally binding commitment to make a
                          gift to the grantee.
                          In addition, this grant recommendation is not made for lobbying purposes or to support a political campaign.
                        </Typography>
                      }
                    />
                    <Typography
                      variant="bodyCopyRegular"
                      color={neutral[900]}
                    ></Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 5 }}>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  sx={{ paddingY: 2, paddingX: 3 }}
                  disabled={
                    !exchangeValue ||
                    !obligationPayment ||
                    (userData?.sub === awsExports.neta_super_admin_user_sub &&
                      !selectedUser)
                  }
                  startIcon={
                    isLoading && <CircularProgress size={20} color="inherit" />
                  }
                  onClick={submit}
                >
                  Next
                </Button>
              </Box>
            </>
          )}

          {activeStep === 3 && charity && (
            <>
              <Grid container spacing={5}>
                <Grid sx={{ mt: 8 }} item xs={12} sm={9}>
                  <GrantSuccess
                    interestedIn={
                      grantRequest.transactionType ===
                        GRANT_REQUEST_TRANSACTION_TYPE_ENUM.CASH
                        ? null
                        : (grantRequest.information as InvestmentInfo)
                          .interestedIn
                    }
                    amount={getAmount()!}
                    charityName={charity?.name}
                    transactionType={grantRequest.transactionType}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}
    </Layout>
  );
};

export default GrantRequestHomePage;
